import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import TableToolbar, { SortableTh } from "@/components/TableToolbar";
import { useTableView } from "@/hooks/useTableView";
import { useAuth } from "@/context/AuthContext";

const empty = (kind) => ({
  kind,
  amount: "",
  currency: "TRY",
  date: new Date().toISOString().slice(0, 10),
  category: "",
  description: "",
  contact_id: "",
  bank_account_id: "",
  status: "paid",
  due_date: "",
  payment_type: "resmi",
  invoice_id: "",
});

export default function TransactionsPage({ kind, title, subtitle, testidPrefix }) {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty(kind));

  const { data: txs = [] } = useQuery({
    queryKey: ["tx", kind],
    queryFn: async () => (await api.get(`/transactions?kind=${kind}`)).data,
  });
  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: async () => (await api.get("/contacts")).data,
  });
  const { data: banks = [] } = useQuery({
    queryKey: ["banks"],
    queryFn: async () => (await api.get("/bank-accounts")).data,
  });

  const [editing, setEditing] = useState(null);

  const view = useTableView({ amountField: "amount_try" });
  const visibleTxs = view.apply(txs);
  const totalTry = visibleTxs.reduce((s, t) => s + (t.amount_try || 0), 0);

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/transactions", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries();
      setOpen(false); setEditing(null); setForm(empty(kind));
      toast.success("Kaydedildi");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Hata"),
  });
  const update = useMutation({
    mutationFn: async ({ id, p }) => (await api.put(`/transactions/${id}`, p)).data,
    onSuccess: () => {
      qc.invalidateQueries();
      setOpen(false); setEditing(null); setForm(empty(kind));
      toast.success("Güncellendi");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Hata"),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/transactions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries(),
  });
  const markPaid = useMutation({
    mutationFn: async (id) => (await api.post(`/transactions/${id}/mark-paid`)).data,
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Ödendi olarak işaretlendi");
    },
  });

  const startEdit = (t) => {
    setEditing(t);
    setForm({
      kind: t.kind,
      amount: String(t.amount ?? ""),
      currency: t.currency || "TRY",
      date: t.date || new Date().toISOString().slice(0, 10),
      category: t.category || "",
      description: t.description || "",
      contact_id: t.contact_id || "",
      bank_account_id: t.bank_account_id || "",
      status: t.status || "paid",
      due_date: t.due_date || "",
      payment_type: t.payment_type || "resmi",
      invoice_id: t.invoice_id || "",
    });
    setOpen(true);
  };
  const startCreate = () => { setEditing(null); setForm(empty(kind)); setOpen(true); };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) };
    if (!payload.contact_id) delete payload.contact_id;
    if (!payload.bank_account_id) delete payload.bank_account_id;
    if (!payload.invoice_id) delete payload.invoice_id;
    if (payload.status === "paid") delete payload.due_date;
    if (editing) update.mutate({ id: editing.id, p: payload });
    else create.mutate(payload);
  };

  // Load invoices for selected contact (to link payment)
  const { data: contactInvoices = [] } = useQuery({
    queryKey: ["contact-invoices", form.contact_id],
    queryFn: async () =>
      form.contact_id
        ? (await api.get(`/contacts/${form.contact_id}/invoices`)).data
        : [],
    enabled: !!form.contact_id,
  });
  const matchDir = kind === "income" ? "outgoing" : "incoming";
  const linkableInvoices = contactInvoices.filter(
    (inv) =>
      inv.direction === matchDir &&
      (inv.status === "pending" || inv.id === form.invoice_id)
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid={`${testidPrefix}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">{subtitle}</div>
          <h1 className="mt-1 text-3xl font-black tracking-tight">{title}</h1>
        </div>
        {isAdmin && (
          <Button data-testid={`${testidPrefix}-add-btn`} onClick={startCreate}>
            <Plus size={16} className="mr-1" /> Yeni Kayıt
          </Button>
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editing
                  ? (title === "Gelirler" ? "Gelir Düzenle" : "Gider Düzenle")
                  : (title === "Gelirler" ? "Yeni Gelir" : "Yeni Gider")}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tutar</Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid={`${testidPrefix}-amount`}
                  />
                </div>
                <div>
                  <Label>Para Birimi</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => setForm({ ...form, currency: v })}
                  >
                    <SelectTrigger data-testid={`${testidPrefix}-currency`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY">TRY (₺)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    required
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Durum</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => setForm({ ...form, status: v })}
                  >
                    <SelectTrigger data-testid={`${testidPrefix}-status`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Ödendi</SelectItem>
                      <SelectItem value="pending">Bekliyor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.status === "pending" && (
                <div>
                  <Label>Vade Tarihi</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  />
                </div>
              )}
              <div>
                <Label>Kategori</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder={kind === "income" ? "Satış, Danışmanlık..." : "Kira, Fatura..."}
                />
              </div>
              <div>
                <Label>Ödeme Türü</Label>
                <Select
                  value={form.payment_type}
                  onValueChange={(v) => setForm({ ...form, payment_type: v })}
                >
                  <SelectTrigger data-testid={`${testidPrefix}-paymenttype`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resmi">Resmi</SelectItem>
                    <SelectItem value="aciktan">Açıktan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cari (opsiyonel)</Label>
                <Select
                  value={form.contact_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, contact_id: v === "__none__" ? "" : v, invoice_id: "" })}
                >
                  <SelectTrigger data-testid={`${testidPrefix}-contact`}><SelectValue placeholder="Cari seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Yok —</SelectItem>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.contact_id && linkableInvoices.length > 0 && (
                <div>
                  <Label>Fatura (opsiyonel)</Label>
                  <Select
                    value={form.invoice_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, invoice_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid={`${testidPrefix}-invoice`}><SelectValue placeholder="Fatura seç" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Yok —</SelectItem>
                      {linkableInvoices.map((inv) => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.number || fmtDate(inv.date)} · {fmtMoney(inv.amount, inv.currency)} · kalan {fmtMoney(inv.remaining_try, "TRY")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-1 text-[11px] text-muted-foreground">Fatura ödenen toplam brüte ulaştığında otomatik "Ödendi" olur.</div>
                </div>
              )}
              <div>
                <Label>Banka Hesabı (opsiyonel)</Label>
                <Select
                  value={form.bank_account_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, bank_account_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Banka seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Yok —</SelectItem>
                    {banks.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name} ({b.currency})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Açıklama</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid={`${testidPrefix}-save`}>{editing ? "Güncelle" : "Kaydet"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <TableToolbar
        filters={view.filters}
        setFilters={view.setFilters}
        onReset={view.resetFilters}
        testidPrefix={testidPrefix}
        showPaymentType={true}
        countInfo={`${visibleTxs.length} / ${txs.length} kayıt`}
        csvExport={{
          filename: `${kind === "income" ? "gelirler" : "giderler"}-${new Date().toISOString().slice(0, 10)}.csv`,
          columns: [
            { label: "Tarih", value: (t) => fmtDate(t.date) },
            { label: "Kategori", value: (t) => t.category || "" },
            { label: "Ödeme Türü", value: (t) => ((t.payment_type || "resmi") === "resmi" ? "Resmi" : "Açıktan") },
            { label: "Cari", value: (t) => contacts.find((c) => c.id === t.contact_id)?.name || "" },
            { label: "Açıklama", value: (t) => t.description || "" },
            { label: "Tutar", value: (t) => t.amount },
            { label: "Para Birimi", value: (t) => t.currency || "TRY" },
            { label: "TL Karşılığı", value: (t) => t.amount_try },
            { label: "Durum", value: (t) => (t.status === "paid" ? "Ödendi" : "Bekliyor") },
          ],
          rows: visibleTxs,
          totalRow: ["", "", "", "", "", "", "GÖRÜNTÜLENEN TOPLAM (TL)", totalTry, ""],
        }}
      />

      <div className="mt-4 overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[760px] text-sm" data-testid={`${testidPrefix}-table`}>
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-xs uppercase tracking-wider text-muted-foreground">
              <SortableTh label="Tarih" sortKey="date" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-date`} />
              <SortableTh label="Kategori" sortKey="category" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-category`} />
              <SortableTh label="Ödeme Türü" sortKey="payment_type" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-paymenttype`} />
              <th className="px-4 py-3 text-left">Cari</th>
              <SortableTh label="Açıklama" sortKey="description" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-desc`} />
              <SortableTh label="Tutar" sortKey="amount" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid={`${testidPrefix}-sort-amount`} />
              <SortableTh label="TL Karşılığı" sortKey="amount_try" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} align="right" testid={`${testidPrefix}-sort-amount_try`} />
              <SortableTh label="Durum" sortKey="status" currentKey={view.sortKey} currentDir={view.sortDir} onToggle={view.toggleSort} testid={`${testidPrefix}-sort-status`} />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visibleTxs.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {txs.length === 0 ? "Henüz kayıt yok." : "Filtrelere uyan kayıt yok."}
                </td>
              </tr>
            )}
            {visibleTxs.map((t) => {
              const contact = contacts.find((c) => c.id === t.contact_id);
              return (
                <tr key={t.id} className="border-b border-border/60 hover:bg-secondary/30">
                  <td className="px-4 py-3 mono">{fmtDate(t.date)}</td>
                  <td className="px-4 py-3">{t.category || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold ${
                      (t.payment_type || "resmi") === "resmi"
                        ? "bg-blue-50 text-blue-700"
                        : "bg-amber-50 text-amber-700"
                    }`} data-testid={`${testidPrefix}-paymenttype-${t.id}`}>
                      {(t.payment_type || "resmi") === "resmi" ? "Resmi" : "Açıktan"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{contact?.name || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.description || "-"}</td>
                  <td className="px-4 py-3 mono text-right">{fmtMoney(t.amount, t.currency)}</td>
                  <td className="px-4 py-3 mono text-right text-muted-foreground">
                    {fmtMoney(t.amount_try, "TRY")}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-sm px-2 py-0.5 text-xs ${
                        t.status === "paid"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {t.status === "paid" ? "Ödendi" : "Bekliyor"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && <div className="flex justify-end gap-2">
                      {t.status === "pending" && (
                        <button
                          onClick={() => markPaid.mutate(t.id)}
                          data-testid={`markpaid-${t.id}`}
                          className="rounded border border-border p-1 hover:bg-secondary"
                          title="Ödendi işaretle"
                        >
                          <CheckCircle size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => startEdit(t)}
                        data-testid={`edit-tx-${t.id}`}
                        className="rounded border border-border p-1 hover:bg-secondary"
                        title="Düzenle"
                      >
                        <PencilSimple size={14} />
                      </button>
                      <button
                        onClick={() => remove.mutate(t.id)}
                        data-testid={`del-tx-${t.id}`}
                        className="rounded border border-border p-1 text-destructive hover:bg-destructive/5"
                      >
                        <Trash size={14} />
                      </button>
                    </div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {visibleTxs.length > 0 && (
            <tfoot className="border-t-2 border-border bg-secondary/40">
              <tr>
                <td colSpan={6} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Görüntülenen Toplam (TL)
                </td>
                <td
                  className={`px-4 py-3 mono text-right text-base font-black ${
                    kind === "income" ? "text-emerald-700" : "text-red-600"
                  }`}
                  data-testid={`${testidPrefix}-total-try`}
                >
                  {fmtMoney(totalTry)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
